import { Injectable } from '@angular/core';
import { forkJoin, Observable, of, throwError, firstValueFrom } from 'rxjs';
import { catchError, finalize, map, shareReplay, tap } from 'rxjs/operators';

import { CategoryService } from 'app/services/category/category.service';
import { BrandService } from 'app/services/brand/brand.service';
import { UnitService } from 'app/services/units/unit.service';
import { VariantService } from 'app/services/variant/variant.service';
import { StoreService } from 'app/services/store/store.service';

export interface LookupOption {
  id: number | string;
  name: string;
}

export interface ProductLookups {
  mainCategories: LookupOption[];
  subCategories: LookupOption[];
  brands: LookupOption[];
  units: LookupOption[];
  variants: LookupOption[];
  stores: LookupOption[];
}

interface LookupCacheStorage {
  savedAt: number;
  data: ProductLookups;
}

@Injectable({
  providedIn: 'root'
})
export class LookupCacheService {
  private readonly cacheKey = 'product_lookups_cache_v2';
  private readonly ttlMs = 24 * 60 * 60 * 1000;

  private memoryCache: ProductLookups | null = null;
  private loadingRequest$: Observable<ProductLookups> | null = null;

  constructor(
    private categoryService: CategoryService,
    private brandService: BrandService,
    private unitService: UnitService,
    private variantService: VariantService,
    private storeService: StoreService
  ) {}

  init(): Promise<void> {
    return firstValueFrom(this.getLookups())
      .then(() => undefined)
      .catch(error => {
        console.error('Lookup cache init failed:', error);
        return undefined;
      });
  }

  getLookups(forceRefresh = false): Observable<ProductLookups> {
    if (!forceRefresh && this.memoryCache) {
      return of(this.memoryCache);
    }

    const cached = this.readCache();

    if (!forceRefresh && cached) {
      this.memoryCache = cached;
      return of(cached);
    }

    if (!forceRefresh && this.loadingRequest$) {
      return this.loadingRequest$;
    }

    this.loadingRequest$ = forkJoin({
      mainCategories: this.categoryService.getMainCategories(),
      subCategories: this.categoryService.getSubCategories(),
      brands: this.brandService.getBrands(0),
      units: this.unitService.getUnits(0),
      variants: this.variantService.getVariants(0),
      stores: this.storeService.getStores(0)
    }).pipe(
      map((res: any) => {
        const data: ProductLookups = {
          mainCategories: this.normalizeOptions(res.mainCategories?.data || []),
          subCategories: this.normalizeOptions(res.subCategories?.data || []),
          brands: this.normalizeOptions(res.brands?.data || []),
          units: this.normalizeOptions(res.units?.data || []),
          variants: this.normalizeOptions(res.variants?.data || []),
          stores: this.normalizeOptions(res.stores?.data || [])
        };

        return data;
      }),
      tap(data => {
        this.memoryCache = data;
        this.saveCache(data);
      }),
      catchError(error => {
        const oldCache = this.readCache(true);

        if (oldCache) {
          this.memoryCache = oldCache;
          return of(oldCache);
        }

        return throwError(() => error);
      }),
      finalize(() => {
        this.loadingRequest$ = null;
      }),
      shareReplay(1)
    );

    return this.loadingRequest$;
  }

  refreshLookups(): Observable<ProductLookups> {
    return this.getLookups(true);
  }

  clearCache(): void {
    this.memoryCache = null;
    localStorage.removeItem(this.cacheKey);
  }

  private saveCache(data: ProductLookups): void {
    const payload: LookupCacheStorage = {
      savedAt: Date.now(),
      data
    };

    localStorage.setItem(this.cacheKey, JSON.stringify(payload));
  }

  private readCache(ignoreExpiry = false): ProductLookups | null {
    const raw = localStorage.getItem(this.cacheKey);

    if (!raw) return null;

    try {
      const parsed: LookupCacheStorage = JSON.parse(raw);

      if (!parsed?.data || !parsed?.savedAt) {
        return null;
      }

      const isExpired = Date.now() - parsed.savedAt > this.ttlMs;

      if (isExpired && !ignoreExpiry) {
        localStorage.removeItem(this.cacheKey);
        return null;
      }

      return parsed.data;
    } catch {
      localStorage.removeItem(this.cacheKey);
      return null;
    }
  }

  private normalizeOptions(items: any[]): LookupOption[] {
    return items.map(item => ({
      id: item.id,
      name: this.getDisplayName(item)
    }));
  }

  private getDisplayName(item: any): string {
    if (!item) return '';

    return (
      item.name ??
      item.arName ??
      item.enName ??
      item.ar_name ??
      item.en_name ??
      item.store_name ??
      item.storeName ??
      item.title ??
      item.category_translations?.[0]?.name ??
      item.categoryTranslations?.[0]?.name ??
      item.brand_translations?.[0]?.name ??
      item.brandTranslations?.[0]?.name ??
      item.unit_translations?.[0]?.name ??
      item.unitTranslations?.[0]?.name ??
      item.variant_translations?.[0]?.name ??
      item.variantTranslations?.[0]?.name ??
      `#${item.id}`
    );
  }
}